/**
 * Sodium high-bay geometry only; the hall's lighting rig supplies the actual
 * amber light. Like `ceiling-tube`, its origin is the ceiling-side reflector
 * mouth and it grows upward, so placement can turn the complete fitting over
 * without every hung part needing an exception.
 */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone, type ZonePalette } from '@/render/palette';

const zone: ZonePalette = (() => {
  const found = getZone('fattening');
  if (!found) throw new Error('fattening zone missing from the palette');
  return found;
})();

function colour(index: number): number {
  const value = zone.surfaces[index];
  if (value === undefined) throw new Error(`fattening palette surface ${String(index)} missing`);
  return value;
}

function wireBetween(start: THREE.Vector3, end: THREE.Vector3, material: THREE.Material, name: string): THREE.Mesh {
  const direction = new THREE.Vector3().subVectors(end, start);
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, direction.length(), 3, 1, true), material);
  mesh.name = name;
  mesh.position.copy(start).add(end).multiplyScalar(0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  mesh.castShadow = true;
  return mesh;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'high-bay-lamp';

  const darkMetal = surface('metal', colour(1));
  const metal = surface('metal', colour(2));
  const reflectorMaterial = surface('metal', colour(4));

  // The lathed shell remains open at both ends: a capped grey disc made the
  // fitting read like a speaker in the first silhouette pass rather than a lamp.
  const reflector = new THREE.Mesh(
    new THREE.LatheGeometry([
      new THREE.Vector2(0.23, 0.02),
      new THREE.Vector2(0.105, 0.17),
      new THREE.Vector2(0.07, 0.22),
      new THREE.Vector2(0.078, 0.24),
    ], 6),
    reflectorMaterial,
  );
  reflector.name = 'open-spun-reflector';
  reflector.castShadow = true;
  group.add(reflector);

  // The capsule is albedo only. A light here would multiply at every placement;
  // the fattening hall owns illumination so its sodium haze stays coherent.
  const capsule = new THREE.Mesh(new THREE.CapsuleGeometry(0.0275, 0.135, 3, 6), surface('mineral', zone.key));
  capsule.name = 'switched-on-sodium-capsule';
  capsule.position.y = 0.145;
  capsule.castShadow = true;
  group.add(capsule);

  const ringHeights = [0.006, 0.12, 0.22] as const;
  const ringRadii = [0.22, 0.15, 0.07] as const;
  for (const [index, radius] of ringRadii.entries()) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.006, 3, 6), metal);
    ring.name = `guard-ring-${index + 1}`;
    ring.rotation.x = Math.PI / 2;
    ring.position.y = ringHeights[index] ?? 0;
    ring.castShadow = true;
    group.add(ring);
  }
  for (let index = 0; index < 6; index += 1) {
    const angle = index * Math.PI / 3;
    group.add(wireBetween(
      new THREE.Vector3(Math.cos(angle) * 0.22, 0.006, Math.sin(angle) * 0.22),
      new THREE.Vector3(Math.cos(angle) * 0.07, 0.22, Math.sin(angle) * 0.07),
      metal,
      `guard-radial-${index + 1}`,
    ));
  }

  const gearBox = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.1, 0.14), darkMetal);
  gearBox.name = 'gear-box';
  gearBox.position.set(0.025, 0.29, 0);
  gearBox.castShadow = true;
  const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.18, 6), metal);
  stem.name = 'hanging-stem';
  stem.position.set(0, 0.43, 0);
  stem.castShadow = true;
  const plate = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.01, 6), darkMetal);
  plate.name = 'ceiling-plate';
  plate.position.y = 0.515;
  plate.castShadow = true;
  group.add(gearBox, stem, plate);

  return group;
}

export const highBayLampAsset: AssetDefinition = {
  id: 'high-bay-lamp',
  label: 'Sodium high-bay',
  description: 'Open spun reflector, sodium capsule, and wire guard for the fattening hall; illumination belongs to the scene.',
  footprint: { width: 0.46, height: 0.52, length: 0.46 },
  create: build,
};
